/**
 * Yumway AI Assistant API Handler
 * This can be used as a backend API endpoint or serverless function
 * Location: /api/site-chat or as a helper utility
 */

import { WEBSITE_DETAILS, KEYWORDS } from './siteChatLocal';

// Pages database with keywords and summaries
const PAGES_DATABASE = [
  {
    path: '/',
    title: 'Home',
    summary: 'View Yumway home page highlights, gallery, and latest product previews.',
    keywords: ['home', 'gallery', 'photos', 'homepage', 'main'],
    content: 'Home page with product gallery and company highlights'
  },
  {
    path: '/product',
    title: 'Products',
    summary: 'Browse Yumway main product categories: French Fries, Value Added Products, and Potato Flakes.',
    keywords: ['product', 'products', 'category', 'portfolio', 'offering', 'range'],
    content: 'Explore our core product categories'
  },
  {
    path: '/frenchfries',
    title: 'French Fries',
    summary: 'Crispy golden French Fries made from high-quality potatoes, ideal for foodservice and retail.',
    keywords: ['french fries', 'fries', 'potato fries', 'crispy', 'golden'],
    content: 'French fries product details'
  },
  {
    path: '/potatoflakesandmashed',
    title: 'Potato Flakes & Mashed',
    summary: 'Instant potato flakes and mashed potato products for easy preparation and consistent quality.',
    keywords: ['potato flakes', 'mashed potato', 'instant', 'convenient'],
    content: 'Potato flakes and mashed potato products'
  },
  {
    path: '/ourspecialty',
    title: 'Value Added Products',
    summary: 'Value-added potato specialties such as burger patties, hash browns, and wedges for ready-to-cook use.',
    keywords: ['value added', 'specialty', 'burger patty', 'hash brown', 'aloo tikki', 'potato wedges'],
    content: 'Value added product offerings'
  },
  {
    path: '/about',
    title: 'About Yumway',
    summary: 'Learn about Yumway Food, our mission, and our commitment to quality frozen foods and export-ready products.',
    keywords: ['about', 'company', 'mission', 'vision', 'background', 'yumway'],
    content: 'Company information and background'
  },
  {
    path: '/exports',
    title: 'Exports',
    summary: 'Learn about Yumway export operations, global shipping, and international business capabilities.',
    keywords: ['export', 'exports', 'international', 'global', 'shipping', 'overseas'],
    content: 'International export operations'
  },
  {
    path: '/quality',
    title: 'Quality',
    summary: 'Discover Yumway quality standards, HACCP compliance, ISO 22000 certification, and cold-chain management.',
    keywords: ['quality', 'certification', 'haccp', 'iso 22000', 'testing', 'assurance'],
    content: 'Quality standards and certifications'
  },
  {
    path: '/contactus',
    title: 'Contact Us',
    summary: 'Get in touch with Yumway through phone, email, or our contact page for inquiries and support.',
    keywords: ['contact', 'email', 'phone', 'address', 'location', 'inquiry'],
    content: 'Contact information and support'
  },
  {
    path: '/blog',
    title: 'Blog',
    summary: 'Read news and updates about Yumway products and business developments.',
    keywords: ['blog', 'news', 'updates', 'articles'],
    content: 'Blog and news updates'
  },
  {
    path: '/privacypolicy',
    title: 'Privacy Policy',
    summary: 'Read Yumway privacy policy and how personal data is protected.',
    keywords: ['privacy', 'policy', 'data', 'protection'],
    content: 'Privacy policy information'
  },
  {
    path: '/termsandCondition',
    title: 'Terms & Conditions',
    summary: 'Review the terms and conditions for using this website and Yumway services.',
    keywords: ['terms', 'conditions', 'agreement', 'legal'],
    content: 'Terms and conditions'
  }
];

/**
 * Tokenize question
 */
function tokenizeQuestion(question) {
  return question
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .filter(token => token.length > 2 && !KEYWORDS.STOP_WORDS.includes(token));
}

/**
 * Score pages based on relevance
 */
function scorePages(question) {
  const tokens = tokenizeQuestion(question);
  
  return PAGES_DATABASE.map(page => {
    let score = 0;

    // Title score (highest weight)
    const titleTokens = tokenizeQuestion(page.title);
    tokens.forEach(token => {
      if (titleTokens.some(t => t.includes(token) || token.includes(t))) {
        score += 10;
      }
    });

    // Summary score (medium weight)
    const summaryTokens = tokenizeQuestion(page.summary);
    tokens.forEach(token => {
      if (summaryTokens.some(t => t.includes(token) || token.includes(t))) {
        score += 5;
      }
    });

    // Keywords score (high weight)
    tokens.forEach(token => {
      if (page.keywords.some(keyword => keyword.includes(token) || token.includes(keyword))) {
        score += 8;
      }
    });

    return { ...page, score };
  }).filter(page => page.score > 0);
}

/**
 * Build friendly response based on intent and question
 */
function buildResponse(question, relevantPages) {
  const lowerQuestion = question.toLowerCase();

  if (relevantPages.length === 0) {
    return {
      answer: 'I didn\'t quite understand your question. Please contact us for more discussion and support.',
      links: [
        { title: 'Browse All Products', href: '/product' },
        { title: 'Contact Us', href: '/contactus' }
      ],
      needsClarification: true
    };
  }

  // Sort by score
  const sorted = relevantPages.sort((a, b) => b.score - a.score);
  const topPages = sorted.slice(0, 3);

  // Build answer based on number of results
  let answer = '';

  if (topPages.length === 1) {
    answer = `Great question! Here's what I found: ${topPages[0].summary}`;
  } else if (topPages.length === 2) {
    answer = `I found a couple of relevant pages for you:\n\n1. ${topPages[0].title}: ${topPages[0].summary}\n\n2. ${topPages[1].title}: ${topPages[1].summary}`;
  } else {
    answer = `I found several relevant pages:\n\n1. ${topPages[0].title}: ${topPages[0].summary}\n\n2. ${topPages[1].title}: ${topPages[1].summary}\n\n3. ${topPages[2].title}: ${topPages[2].summary}`;
  }

  const links = topPages.map(page => ({
    title: page.title,
    href: page.path
  }));

  return { answer, links };
}

/**
 * Main API handler function
 */
export async function handleSiteChatAPI(question) {
  if (!question || question.trim().length === 0) {
    return {
      answer: 'Please ask me a question about Yumway\'s products, services, or company information.',
      links: [{ title: 'Browse Products', href: '/product' }]
    };
  }

  // Check for restricted keywords
  const hasRestricted = KEYWORDS.RESTRICTED_KEYWORDS.some(keyword =>
    question.toLowerCase().includes(keyword)
  );

  if (hasRestricted) {
    return {
      answer: 'I can help with questions about Yumway food products, services, and company information. For medical or health-related queries, please consult appropriate professionals.',
      links: []
    };
  }

  // Score and rank pages
  const scoredPages = scorePages(question);

  // Build response
  return buildResponse(question, scoredPages);
}

/**
 * Export API handler for use in Node.js backend
 * Usage: In your Express/Node backend
 * 
 * import { handleSiteChatAPI } from './siteChatAPI.js';
 * 
 * app.post('/api/site-chat', async (req, res) => {
 *   const { question } = req.body;
 *   const response = await handleSiteChatAPI(question);
 *   res.json(response);
 * });
 */
